import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDatePicker,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { CurrentUser, TermSheetRevision, TermSheetTypes, TermSheetTypesMap } from '../../../shared/models';
import { FormikUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';
import { validateDateWithinPlusMinus100Years } from '../../../shared/utils/date';

interface AddRevisionFormProps {
	dataRoomGuid: string | undefined;
	revision: TermSheetRevision | null;
	currentUser: CurrentUser;
	close: () => void;
}

export const AddRevisionForm: React.FC<AddRevisionFormProps> = ({ dataRoomGuid, revision, currentUser, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const addRevisionMutation = useMutation(
		async ({ values }: AddRevisionFormMutationVariables) => {
			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				let formData = new FormData();
				formData.append('dateReceived', values.dateReceived.toISOString());
				formData.append('notifyBorrower', values.notifyBorrower ? 'true' : 'false');
				formData.append('termSheetType', values.termSheetType);

				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});

				const { data, error } = await getApiResponseData<void>(
					api.post(
						CerebroApiEndpoints.TermSheets.AddRevision(
							dataRoomGuid,
							revision?.termSheetGuid,
							revision?.guid
						),
						formData,
						{
							headers: { 'Content-Type': 'multipart/form-data' },
						}
					)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			return null;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Term sheet was added.', { variant: 'success' });
				client.invalidateQueries(QueryKeys.TermSheets.List(dataRoomGuid));
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={revision != null} onClose={close}>
			<CerebroHeading>Add Term Sheet Revision</CerebroHeading>

			<Formik
				initialValues={{
					notifyBorrower: currentUser.isEmployeeUser,
					dateReceived: new Date(),
					termSheetType: 'TermSheet' as TermSheetTypes,
					uploads: [] as File[],
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(addRevisionMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm
							flatIndicator
							encType="multipart/form-data"
							onSubmit={handleSubmit}
							sx={{ marginTop: 3, paddingRight: 3 }}
						>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel required id="uploads">
										Term Sheet File
									</CerebroLabel>
									<Field
										as={CerebroFileDragAndDrop}
										name="uploads"
										onlyPdf={true}
										multiple={false}
										sx={{ marginY: 3, width: '100%' }}
										error={touched.uploads && errors.uploads !== undefined}
									/>
									{errors.uploads && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.uploads}
										</CerebroText>
									)}
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel id="termSheetType">Term Sheet Type</CerebroLabel>
									<Field
										id="termSheetType"
										as={CerebroDropdown}
										name="termSheetType"
										disabled={isSubmitting}
									>
										{Object.entries(TermSheetTypesMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								</Grid>
								<Grid item xs={11}>
									<CerebroLabel id="dateReceived">Date Received</CerebroLabel>
									<Field
										id="dateReceived"
										name="dateReceived"
										component={CerebroDatePicker}
										disabled={isSubmitting}
										error={touched.dateReceived && errors.dateReceived !== undefined}
									/>
									{touched.dateReceived && errors.dateReceived && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.dateReceived}
										</CerebroText>
									)}
								</Grid>
								{currentUser.isEmployeeUser && (
									<Grid item xs={11}>
										<CerebroLabel id="notifyBorrower">Notify Borrower</CerebroLabel>
										<Field
											id="notifyBorrower"
											as={CerebroYesNo}
											name="notifyBorrower"
											disabled={isSubmitting}
										/>
									</Grid>
								)}

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type AddRevisionFormMutationVariables = FormikUtils.MutateOnSubmitVariables<AddRevisionFormValues>;
interface AddRevisionFormValues {
	notifyBorrower: boolean;
	dateReceived: Date;
	termSheetType: TermSheetTypes;
	uploads: File[];
}
type AddRevisionFormValueErrors = FormikErrors<AddRevisionFormValues>;

function validateForm(values: AddRevisionFormValues) {
	const errors: AddRevisionFormValueErrors = {};

	if (!values.uploads || values.uploads.length == 0) {
		errors.uploads = 'You must select a term sheet file.';
	}

	if (!values.dateReceived) {
		errors.dateReceived = 'You must select a valid date the term sheet was received.';
	} else {
		const dateReceivedError = validateDateWithinPlusMinus100Years(values.dateReceived, 'Date Received');
		if (dateReceivedError) {
			errors.dateReceived = dateReceivedError;
		}
	}

	return errors;
}
